/**
 * "Failed payments" — `mockups/studio/money.html` §what did not settle, and the
 * ageing chart beside it.
 *
 * Three buttons on a row, and which one it gets comes from the row rather than from a
 * list: a charge with a retry still to come gets **Email them** and **Cancel retry**;
 * one whose schedule has run out gets **Chase**, which is the same mail worded for
 * "nobody is going to try again". A row that has already recovered gets neither,
 * because there is nothing to chase.
 *
 * Both mails are `notifications_send` with the `payment_failed_reminder` template.
 * That template is `HAND_WRITTEN` in `actionFields.ts` — `message` is a discriminated
 * union over twenty templates and a generic form would draw all of them — so this
 * builds the params itself, from the fields already on the row.
 *
 * **The mockup's "Failed" column is not here.** `FailedPaymentSchema` carries
 * `attempt`, `of`, `next_retry_at` and `action_hint`, and no timestamp of the failure
 * itself; the mockup prints "11 Aug". The report asks for `failed_at`.
 */

import { useState } from 'react'

import type { StudioMoneyView } from '@fareast/shared'

import { railLabel } from '../../../components/client/index.js'
import { AgeingChart } from '../../../components/studio/AgeingChart.js'
import { ClientLink } from '../../../components/studio/ClientLink.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Button } from '../../../components/portal/Button.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Grid, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Notice, Pill, Tag } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import {
  DateText,
  Mono,
  Muted,
  Num,
  Prose,
  Ref,
  Small,
  Xs,
} from '../../../components/portal/Text.js'
import { CancelRetryDialog, ReminderDialog } from './FailedActions.js'
import type { PastDue } from './MoneyPage.js'
import type { NameOf } from './names.js'

export type FailedSectionProps = {
  readonly failed: StudioMoneyView['failed_payments']
  readonly ageing: StudioMoneyView['ageing']
  /**
   * What is still owed on each past-due invoice, and for how long — keyed by
   * reference. `analytics_receivables`' own figures; see `pastDueOf`.
   */
  readonly pastDue: ReadonlyMap<string, PastDue>
  readonly nameOf: NameOf
}

export function FailedSection({ failed, ageing, pastDue, nameOf }: FailedSectionProps) {
  const [emailing, setEmailing] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState<string | null>(null)

  const emailingRow = failed.find((one) => one.invoice_ref === emailing) ?? null
  const cancellingRow = failed.find((one) => one.invoice_ref === cancelling) ?? null

  return (
    <Section>
      <SectionHead
        title="Failed payments"
        note="Bank debits report failures by webhook, days later"
      />

      {failed.length === 0 ? (
        <Empty>Nothing has failed to settle.</Empty>
      ) : (
        <Card>
          <CardBody tight scrollX>
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Client</Th>
                  <Th scope="col">Rail</Th>
                  <Th scope="col" right>
                    Amount
                  </Th>
                  <Th scope="col">Code</Th>
                  <Th scope="col">Attempt</Th>
                  <Th scope="col">Next</Th>
                  {/* Blank in the mockup, worded here — see `RerunDialog` for why. */}
                  <Th scope="col" right>
                    Chase it
                  </Th>
                </Tr>
              </THead>
              <TBody>
                {failed.map((row) => {
                  const rail = railLabel(row.rail)
                  const recovered = row.recovered
                  return (
                    <Tr key={row.invoice_ref} variant={recovered ? undefined : 'bad'}>
                      <Td>
                        <ClientLink client={row.client} name={nameOf(row.client)} />
                        {/* `<b>Vela Interiors</b><div class="ref nowrap">FES-2026-049</div>` —
                            the mockup's own second line, and a block in it. */}
                        <Xs block>
                          <Ref value={row.invoice_ref} nowrap />
                        </Xs>
                      </Td>
                      <Td>{rail === null ? null : <Tag>{rail}</Tag>}</Td>
                      <Td right num>
                        <Num value={row.total} />
                      </Td>
                      <Td>
                        {row.code === null ? (
                          <Muted>—</Muted>
                        ) : (
                          <Xs>
                            <Mono>{row.code}</Mono>
                          </Xs>
                        )}
                      </Td>
                      <Td num>{`${String(row.attempt)} of ${String(row.of)}`}</Td>
                      <Td>
                        {row.next_retry_at !== null ? (
                          <Small>
                            {'Retry '}
                            <DateText value={row.next_retry_at} zone="UTC" />
                          </Small>
                        ) : row.exhausted ? (
                          <Pill state="bad">Exhausted</Pill>
                        ) : (
                          /*
                           * `action_hint` is written to be the sentence in this cell,
                           * and it is three things rather than two: "Recovered", "No
                           * further attempt scheduled" — which is a charge that could
                           * still be retried by hand — and the exhausted one above.
                           * Printing "Exhausted" over the middle case told somebody a
                           * charge was finished when `invoices_retry_charge$` would
                           * still have run it.
                           */
                          <Small>
                            <Muted>{row.action_hint}</Muted>
                          </Small>
                        )}
                      </Td>
                      <Td right>
                        {recovered ? null : row.next_retry_at !== null ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => {
                                setEmailing(row.invoice_ref)
                              }}
                            >
                              Email them
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setCancelling(row.invoice_ref)
                              }}
                            >
                              Cancel retry
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="ochre"
                            onClick={() => {
                              setEmailing(row.invoice_ref)
                            }}
                          >
                            Chase
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  )
                })}
              </TBody>
            </Table>
          </CardBody>
        </Card>
      )}

      <Grid cols="2-1" top>
        <Stack>
          <Notice need>
            Work pauses at the number of days past due the studio has set, and pausing is a decision
            somebody makes rather than something that happens on its own. The days each of these has
            run is in the ageing table beside it.
          </Notice>
          <Fold summary="Why the retries are spaced like that">
            <Prose>
              Nacha limits attempts and spacing on ACH, and Stripe retries SEPA insufficient-funds
              twice within 30 days. Two SEPA returns on a brand-new mandate usually means the
              mandate is wrong, not that the money is not there — worth a phone call before a third
              attempt.
            </Prose>
          </Fold>
        </Stack>

        <Card>
          <CardBody>
            <AgeingChart bands={ageing} nameOf={nameOf} />
          </CardBody>
        </Card>
      </Grid>

      {emailingRow === null ? null : (
        <ReminderDialog
          row={emailingRow}
          pastDue={pastDue.get(emailingRow.invoice_ref)}
          exhausted={emailingRow.exhausted}
          clientName={nameOf(emailingRow.client)}
          onClose={() => {
            setEmailing(null)
          }}
        />
      )}

      {cancellingRow === null ? null : (
        <CancelRetryDialog
          row={cancellingRow}
          pastDue={pastDue.get(cancellingRow.invoice_ref)}
          clientName={nameOf(cancellingRow.client)}
          onClose={() => {
            setCancelling(null)
          }}
        />
      )}
    </Section>
  )
}

/*
 * `isRecovered` and `isExhausted` were here, and are now two booleans on the wire.
 *
 * `readmodels/studio-money.ts` computed both to write `action_hint` and published
 * only the sentence, so this file recovered the first with
 * `action_hint.toLowerCase().startsWith('recovered')` and restated the second term for
 * term. The chase mail's default wording is picked from the second, which made a drift
 * a mail telling a client nobody would try again while the schedule still would.
 */
